import React, { useEffect, useState } from 'react';
import { api, PropertyDto, PropertyHistoryItemDto, AdminCustomerListItem } from '../../services/api';
import { Card, Btn, Alert, Spinner, C } from '../../components/UI';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';

// ── Property Records (core primitive) ────────────────────────────────────────────
// Persistent address identity a Job can optionally attach to — see Property's own doc comment
// in Models.cs for the full rationale. List + detail/history, matching the shape of
// EstimateBuilderPanel/InventoryPanel elsewhere in this dashboard.

const occupancyColors: Record<string, [string, string]> = {
    Occupied: [C.greenBg, C.greenMid],
    Vacant: ['#FEF3C7', '#92400E'],
    Unknown: ['#F5F5F5', C.textMuted],
};

export function PropertyPanel() {
    const [properties, setProperties] = useState<PropertyDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [view, setView] = useState<'list' | 'new' | 'detail'>('list');
    const [selected, setSelected] = useState<PropertyDto | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getProperties()
            .then(setProperties)
            .catch(e => setError(e.message || 'Failed to load properties.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const filtered = properties.filter(p =>
        !search.trim() || p.address.toLowerCase().includes(search.trim().toLowerCase()) ||
        p.customerName?.toLowerCase().includes(search.trim().toLowerCase()));

    if (view === 'new') {
        return (
            <PropertyForm
                onCancel={() => setView('list')}
                onCreated={p => { setProperties(prev => [p, ...prev]); setView('list'); }}
            />
        );
    }

    if (view === 'detail' && selected) {
        return (
            <PropertyDetail
                property={selected}
                onBack={() => setView('list')}
                onUpdated={p => { setProperties(prev => prev.map(x => x.id === p.id ? p : x)); setSelected(p); }}
            />
        );
    }

    return (
        <div style={{ maxWidth: 960 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Properties</h2>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Persistent addresses — see everything that's happened at one property, across jobs</div>
                </div>
                <Btn onClick={() => setView('new')}>+ New property</Btn>
            </div>

            <Alert message={error} />

            <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by address or client…"
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, marginBottom: 16, boxSizing: 'border-box' }}
            />

            {loading ? <Spinner /> : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {filtered.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No properties found.</div>}
                    {filtered.map((p, i) => {
                        const [bg, fg] = occupancyColors[p.occupancyStatus] || occupancyColors.Unknown;
                        return (
                            <div key={p.id} onClick={() => { setSelected(p); setView('detail'); }} style={{
                                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer',
                                borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                            }}>
                                <span style={{
                                    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                                    background: bg, color: fg, flexShrink: 0, minWidth: 72, textAlign: 'center',
                                }}>{p.occupancyStatus}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{p.address}</div>
                                    <div style={{ fontSize: 12, color: C.textSub }}>
                                        {p.customerName ? `Client: ${p.customerName}` : 'No client assigned'}
                                        {p.externalPropertyId ? ` · ${p.externalPropertyId}` : ''}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Card>
            )}
        </div>
    );
}

function PropertyForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (p: PropertyDto) => void }) {
    const [address, setAddress] = useState('');
    const [geo, setGeo] = useState<{ lat: number; lng: number; city?: string; state?: string; postalCode?: string; country?: string } | null>(null);
    const [occupancyStatus, setOccupancyStatus] = useState<'Unknown' | 'Occupied' | 'Vacant'>('Unknown');
    const [customerId, setCustomerId] = useState('');
    const [customers, setCustomers] = useState<AdminCustomerListItem[]>([]);
    const [externalPropertyId, setExternalPropertyId] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => { api.adminGetCustomers().then(setCustomers).catch(() => {}); }, []);

    const create = async () => {
        if (!address.trim() || !geo) { setError('Select an address from the suggestions.'); return; }
        setSaving(true); setError('');
        try {
            const p = await api.createProperty({
                address, lat: geo.lat, lng: geo.lng, city: geo.city, state: geo.state,
                postalCode: geo.postalCode, country: geo.country, occupancyStatus,
                customerId: customerId || null, externalPropertyId: externalPropertyId.trim() || null,
                notes: notes.trim() || null,
            });
            onCreated(p);
        } catch (e: any) {
            setError(e.message || 'Failed to create property.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card style={{ maxWidth: 560, padding: 28 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: C.text }}>New property</h2>
            <Alert message={error} />

            <AddressAutocomplete
                label="Property address"
                value={address}
                onChange={r => { setAddress(r.address); setGeo({ lat: r.lat, lng: r.lng, city: r.city, state: r.state, postalCode: r.postalCode, country: r.country }); }}
                placeholder="123 Main St, Huntsville, AL"
                required
            />

            <div style={{ marginTop: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Occupancy status</label>
                <select value={occupancyStatus} onChange={e => setOccupancyStatus(e.target.value as any)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: '#fff', boxSizing: 'border-box' }}>
                    <option value="Unknown">Unknown</option>
                    <option value="Occupied">Occupied</option>
                    <option value="Vacant">Vacant</option>
                </select>
            </div>

            <div style={{ marginTop: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Client (optional)</label>
                <select value={customerId} onChange={e => setCustomerId(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: '#fff', boxSizing: 'border-box' }}>
                    <option value="">— None —</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
                </select>
            </div>

            <div style={{ marginTop: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>External property ID (optional)</label>
                <input value={externalPropertyId} onChange={e => setExternalPropertyId(e.target.value)} placeholder="e.g. investor loan/property id"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginTop: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box', resize: 'vertical' }} />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={create} disabled={saving}>{saving ? 'Creating…' : 'Create property'}</Btn>
            </div>
        </Card>
    );
}

function PropertyDetail({ property, onBack, onUpdated }: {
    property: PropertyDto; onBack: () => void; onUpdated: (p: PropertyDto) => void;
}) {
    const [history, setHistory] = useState<PropertyHistoryItemDto[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [occupancyStatus, setOccupancyStatus] = useState(property.occupancyStatus);
    const [notes, setNotes] = useState(property.notes ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');

    useEffect(() => {
        setLoadingHistory(true);
        api.getPropertyHistory(property.id).then(setHistory).catch(() => {}).finally(() => setLoadingHistory(false));
    }, [property.id]);

    const save = async () => {
        setSaving(true); setError(''); setMsg('');
        try {
            const updated = await api.updateProperty(property.id, {
                address: property.address, lat: property.lat, lng: property.lng,
                city: property.city, state: property.state, postalCode: property.postalCode,
                country: property.country, occupancyStatus, customerId: property.customerId,
                notes: notes.trim() || null,
            });
            onUpdated(updated);
            setMsg('Saved.');
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ maxWidth: 720 }}>
            <Btn variant="outline" onClick={onBack} style={{ marginBottom: 16 }}>← Back to properties</Btn>
            <Card style={{ padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>{property.address}</div>
                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 20 }}>
                    {property.customerName ? `Client: ${property.customerName}` : 'No client assigned'}
                </div>

                <Alert message={error} />
                {msg && <div style={{ background: C.greenBg, color: C.greenMid, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Occupancy status</label>
                    <select value={occupancyStatus} onChange={e => setOccupancyStatus(e.target.value as any)}
                        style={{ width: 200, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: '#fff' }}>
                        <option value="Unknown">Unknown</option>
                        <option value="Occupied">Occupied</option>
                        <option value="Vacant">Vacant</option>
                    </select>
                </div>

                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Notes</label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box', resize: 'vertical' }} />
                </div>

                <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Btn>
            </Card>

            <Card style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, fontSize: 14, fontWeight: 700, color: C.text }}>
                    Job history
                </div>
                {loadingHistory ? <Spinner /> : history.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No jobs recorded at this property yet.</div>
                ) : (
                    history.map((h, i) => (
                        <div key={h.jobId} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '12px 18px', borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                        }}>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{h.serviceTypeName}</div>
                                <div style={{ fontSize: 12, color: C.textMuted }}>{h.status}</div>
                            </div>
                            <div style={{ fontSize: 12, color: C.textSub }}>
                                {h.completedAt ? `Completed ${new Date(h.completedAt).toLocaleDateString()}`
                                    : h.scheduledDate ? `Scheduled ${new Date(h.scheduledDate).toLocaleDateString()}` : '—'}
                            </div>
                        </div>
                    ))
                )}
            </Card>
        </div>
    );
}
